import React, { useState, useEffect, useCallback, useRef } from 'react';
import { showToast } from '../utils/toast';
import { FiEdit2, FiTrash2, FiPlus, FiPackage, FiSearch, FiToggleLeft, FiToggleRight,
         FiHelpCircle, FiX, FiDownload, FiUpload, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/Configuracion.css';
import '../styles/Productos.css';

const EMPTY_FORM = {
  codigo: '', nombre: '', alias: '',
  familia_id: '', marca_id: '',
  unidad_compra_id: '', unidad_minima_id: '',
  factor_conversion: '1',
  precio_compra: '', precio_venta: '', precio_mayorista: '', precio_minimo: '',
  stock_minimo: '0',
  descripcion: '', imagen: '',
  presentaciones: [],
};

const EMPTY_PRES = { nombre: '', unidad_medida_id: '', factor: '1', precio_venta: '', es_default: false };

const fmtS = (n) => {
  const v = parseFloat(n);
  if (!v) return '—';
  return v.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

export default function ProductosPage() {
  const { user } = useAuth();
  const bizId = user?.business_id || 1;
  const [productos, setProductos] = useState([]);
  const [familias,  setFamilias]  = useState([]);
  const [marcas,    setMarcas]    = useState([]);
  const [unidades,  setUnidades]  = useState([]);
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [modal,     setModal]     = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [newPres,   setNewPres]   = useState(EMPTY_PRES);
  const [saving,    setSaving]    = useState(false);
  const [modalError, setModalError] = useState('');
  const [showHelp,  setShowHelp]  = useState(false);

  /* ── Import Excel ── */
  const fileInputRef   = useRef(null);
  const [importModal,  setImportModal]  = useState(null); // null | { rows, errors }
  const [importing,    setImporting]    = useState(false);
  const [almacenes,    setAlmacenes]    = useState([]);
  const [almacenImport, setAlmacenImport] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [p, f, m, u, alm] = await Promise.all([
        api.get('/api/productos', { params: { todos: 1 } }),
        api.get('/api/productos/catalogos/familias'),
        api.get('/api/productos/catalogos/marcas'),
        api.get(`/api/config/unidades?business_id=${bizId}`),
        api.get('/api/almacenes'),
      ]);
      setProductos(p.data.data || []);
      setFamilias(f.data.data  || []);
      setMarcas(m.data.data    || []);
      setUnidades(u.data.data  || []);
      setAlmacenes(alm.data?.data || []);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? productos.filter(p =>
        [p.nombre, p.alias, p.codigo, p.familia_nombre, p.marca_nombre]
          .some(v => String(v || '').toLowerCase().includes(search.toLowerCase()))
      )
    : productos;

  const openNew = () => {
    setForm({ ...EMPTY_FORM });
    setNewPres({ ...EMPTY_PRES });
    setModalError('');
    setModal({ mode: 'new' });
  };

  const openEdit = async (p) => {
    try {
      const r = await api.get(`/api/productos/${p.id}`);
      const d = r.data.data;
      setForm({
        codigo:            d.codigo            || '',
        nombre:            d.nombre            || '',
        alias:             d.alias             || '',
        familia_id:        String(d.familia_id  || ''),
        marca_id:          String(d.marca_id    || ''),
        unidad_compra_id:  String(d.unidad_compra_id || ''),
        unidad_minima_id:  String(d.unidad_minima_id || ''),
        factor_conversion: String(d.factor_conversion || 1),
        precio_compra:     String(d.precio_compra    || ''),
        precio_venta:      String(d.precio_venta     || ''),
        precio_mayorista:  String(d.precio_mayorista || ''),
        precio_minimo:     String(d.precio_minimo    || ''),
        stock_minimo:      String(d.stock_minimo     || 0),
        descripcion:       d.descripcion || '',
        imagen:            d.imagen      || '',
        presentaciones: (d.presentaciones || []).map(pr => ({
          id:              pr.id,
          nombre:          pr.nombre,
          unidad_medida_id: String(pr.unidad_medida_id || ''),
          factor:          String(pr.factor),
          precio_venta:    pr.precio_venta != null ? String(pr.precio_venta) : '',
          es_default:      !!pr.es_default,
        })),
      });
      setNewPres({ ...EMPTY_PRES });
      setModalError('');
      setModal({ mode: 'edit', id: d.id });
    } catch (e) { setError(e.response?.data?.error || e.message || 'Error al cargar producto'); }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Presentaciones
  const addPresentacion = () => {
    if (!newPres.nombre.trim()) return;
    const pres = { ...newPres };
    // Si es la primera, marcarla como default
    if (form.presentaciones.length === 0) pres.es_default = true;
    setForm(prev => ({ ...prev, presentaciones: [...prev.presentaciones, pres] }));
    setNewPres({ ...EMPTY_PRES });
  };

  const removePres = (idx) => {
    setForm(prev => {
      const list = prev.presentaciones.filter((_, i) => i !== idx);
      // Si se eliminó la default y hay otras, marcar la primera
      if (list.length > 0 && !list.some(p => p.es_default)) list[0].es_default = true;
      return { ...prev, presentaciones: list };
    });
  };

  const setPresDefault = (idx) => {
    setForm(prev => ({
      ...prev,
      presentaciones: prev.presentaciones.map((p, i) => ({ ...p, es_default: i === idx })),
    }));
  };

  const updatePres = (idx, field, value) => {
    setForm(prev => {
      const list = [...prev.presentaciones];
      list[idx] = { ...list[idx], [field]: value };
      return { ...prev, presentaciones: list };
    });
  };

  const handleSave = async () => {
    if (!form.nombre.trim())           return setModalError('El nombre es requerido.');
    if (!form.unidad_compra_id)        return setModalError('La unidad de compra es requerida.');
    if (!form.unidad_minima_id)        return setModalError('La unidad mínima es requerida.');
    if (Number(form.factor_conversion) < 1) return setModalError('El factor de conversión debe ser ≥ 1.');

    setSaving(true); setModalError('');
    try {
      const payload = {
        ...form,
        familia_id:        form.familia_id        ? Number(form.familia_id)        : null,
        marca_id:          form.marca_id          ? Number(form.marca_id)          : null,
        unidad_compra_id:  Number(form.unidad_compra_id),
        unidad_minima_id:  Number(form.unidad_minima_id),
        factor_conversion: Number(form.factor_conversion),
        precio_compra:    Number(form.precio_compra)    || 0,
        precio_venta:     Number(form.precio_venta)     || 0,
        precio_mayorista: Number(form.precio_mayorista) || 0,
        precio_minimo:    Number(form.precio_minimo)    || 0,
        stock_minimo:     Number(form.stock_minimo)     || 0,
        presentaciones: form.presentaciones.map(p => ({
          ...p,
          unidad_medida_id: p.unidad_medida_id ? Number(p.unidad_medida_id) : null,
          factor:           Number(p.factor) || 1,
          precio_venta:     p.precio_venta !== '' ? Number(p.precio_venta) : null,
        })),
      };

      if (modal.mode === 'new') await api.post('/api/productos', payload);
      else                      await api.put(`/api/productos/${modal.id}`, payload);
      await load(); setModal(null);
    } catch (e) {
      setModalError(e.response?.data?.error || e.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try { await api.patch(`/api/productos/${id}/activo`); await load(); }
    catch (e) { showToast(e.response?.data?.error || 'Error'); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.nombre}"?\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/api/productos/${p.id}`);
      showToast(`"${p.nombre}" eliminado`, 'success');
      await load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Error al eliminar', 'error');
    }
  };

  /* ── Descargar plantilla Excel ── */
  const handleDescargarPlantilla = async () => {
    try {
      const r = await api.get('/api/importar/productos/plantilla', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_productos.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      showToast('Error al descargar la plantilla');
    }
  };

  /* ── Seleccionar archivo y validar ── */
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    if (!file) return;

    const fd = new FormData();
    fd.append('archivo', file);
    try {
      const r = await api.post('/api/importar/productos/validar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportModal({ rows: r.data.rows || [], errors: r.data.errors || [], file });
    } catch (e) {
      showToast(e.response?.data?.error || 'Error al leer el archivo');
    }
  };

  /* ── Confirmar importación ── */
  const handleConfirmImport = async () => {
    if (!importModal?.file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('archivo', importModal.file);
    if (almacenImport) fd.append('almacen_id', almacenImport);
    try {
      const r = await api.post('/api/importar/productos', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { creados, con_stock = 0, errores = [] } = r.data;
      if (errores.length > 0) {
        setImportModal(prev => ({
          ...prev,
          file: null,
          errors: errores.map(e => ({ fila: e.fila, nombre: e.nombre, errores: [e.error] })),
          rows: [],
          postImport: { creados, con_stock },
        }));
      } else {
        const stockMsg = con_stock > 0 ? `, ${con_stock} con stock inicial asignado` : '';
        showToast(`${creados} producto(s) importados correctamente${stockMsg}`, 'success');
        setImportModal(null);
        setAlmacenImport('');
      }
      await load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Error al importar');
    } finally { setImporting(false); }
  };

  const thS = { padding:'6px 10px', textAlign:'left', fontWeight:700, fontSize:11,
                color:'#1e293b', borderBottom:'2px solid #e2e8f0' };
  const tdS = { padding:'6px 10px', borderBottom:'1px solid #f0f4f8', verticalAlign:'top' };

  return (
    <div className="prod-page">
      {/* ── TOOLBAR ── */}
      <div className="pt-toolbar">
        <button className="pt-btn-nuevo" onClick={openNew}><FiPlus /> Nuevo Producto</button>
        <button className="pt-btn-plantilla" onClick={handleDescargarPlantilla} title="Descargar plantilla Excel para carga masiva">
          <FiDownload size={13} /> Plantilla
        </button>
        <button className="pt-btn-importar" onClick={() => fileInputRef.current?.click()} title="Importar productos desde Excel">
          <FiUpload size={13} /> Importar Excel
        </button>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
          onChange={handleFileSelect} />
        <div className="pt-search-wrap">
          <FiSearch size={13} style={{ color: '#94a3b8' }} />
          <input className="pt-search" placeholder="Buscar por nombre, alias, código, familia..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <span className="pt-count">{filtered.length} producto(s)</span>
      </div>

      {/* ── TABLE ── */}
      {loading ? <div className="pt-loading">Cargando...</div>
       : error  ? <div className="pt-error">{error}</div>
       : (
        <div className="prod-table-wrap">
          <table className="prod-table">
            <thead>
              <tr>
                <th className="pt-th pt-cod">Cód. Barra</th>
                <th className="pt-th pt-col-nombre">Producto / Marca</th>
                <th className="pt-th pt-familia">Familia</th>
                <th className="pt-th pt-col-und">Und Comp.</th>
                <th className="pt-th pt-col-und">Und Min</th>
                <th className="pt-th pt-precio">P.Compra</th>
                <th className="pt-th pt-precio">P.Venta</th>
                <th className="pt-th pt-precio">P.Mayor</th>
                <th className="pt-th pt-precio">P.Selecto</th>
                <th className="pt-th pt-col-est">Estado</th>
                <th className="pt-th pt-acc"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan={11} className="pt-empty">
                    {search ? 'Sin resultados' : <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}><FiPackage />Sin productos registrados</span>}
                  </td></tr>
                : filtered.map((p, i) => {
                    return (
                      <tr key={p.id}
                        className={`pt-row ${i % 2 === 0 ? 'pt-row-even' : 'pt-row-odd'}`}>
                        <td className="pt-td pt-td-cod">
                          {p.codigo || <span className="pt-nil">—</span>}
                        </td>
                        <td className="pt-td pt-td-nombre">
                          <span className="pt-nombre">{p.nombre}</span>
                          {p.marca_nombre && <span className="pt-marca">{p.marca_nombre}</span>}
                        </td>
                        <td className="pt-td pt-td-fam">{p.familia_nombre || <span className="pt-nil">—</span>}</td>
                        <td className="pt-td pt-td-center">
                          <span className="pt-und-c">{p.unidad_compra_short || p.unidad_compra_nombre || '—'}</span>
                        </td>
                        <td className="pt-td pt-td-center">
                          <span className="pt-und">{p.unidad_minima_short || p.unidad_minima_nombre || '—'}</span>
                        </td>
                        <td className="pt-td pt-td-num"><span className={!parseFloat(p.precio_compra) ? 'pt-nil' : ''}>{fmtS(p.precio_compra)}</span></td>
                        <td className="pt-td pt-td-num"><span className={!parseFloat(p.precio_venta) ? 'pt-nil' : ''}>{fmtS(p.precio_venta)}</span></td>
                        <td className="pt-td pt-td-num"><span className={!parseFloat(p.precio_mayorista) ? 'pt-nil' : ''}>{fmtS(p.precio_mayorista)}</span></td>
                        <td className="pt-td pt-td-num"><span className={!parseFloat(p.precio_minimo) ? 'pt-nil' : ''}>{fmtS(p.precio_minimo)}</span></td>
                        <td className="pt-td pt-td-center">
                          <span className={`pt-est ${p.activo ? 'pt-est-activo' : 'pt-est-inactivo'}`}>
                            {p.activo ? 'ACTIVO' : 'INACTIVO'}
                          </span>
                        </td>
                        <td className="pt-td pt-td-acc">
                          <div className="ia-actions">
                            <button className="ia-btn ia-btn--edit" onClick={() => openEdit(p)} title="Editar"><FiEdit2 size={14}/></button>
                            <button
                              className={`ia-btn ${p.activo ? 'ia-btn--on' : 'ia-btn--off'}`}
                              onClick={() => handleToggle(p.id)}
                              title={p.activo ? 'Desactivar' : 'Activar'}
                            >{p.activo ? <FiToggleLeft size={14}/> : <FiToggleRight size={14}/>}</button>
                            <button className="ia-btn ia-btn--danger" onClick={() => handleDelete(p)} title="Eliminar"><FiTrash2 size={14}/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              }
            </tbody>
          </table>
        </div>
      )}

      <div className="pt-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>

      {/* ══ MODAL ══ */}
      {modal && (
        <div className="cfg-modal-overlay">
          <div className="cfg-modal prod-modal" onClick={e => e.stopPropagation()} style={{ position:'relative' }}>
            <div className="cfg-modal-header">
              <span>{modal.mode === 'new' ? 'Nuevo Producto' : 'Editar Producto'}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  title="Ver ejemplo de carga"
                  onClick={() => setShowHelp(true)}
                  style={{ background: '#38bdf8', border: '2px solid #7dd3fc', borderRadius: 6,
                           color: '#0c4a6e', cursor: 'pointer', display: 'flex', alignItems: 'center',
                           gap: 4, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}
                >
                  <FiHelpCircle size={14} /> Ayuda
                </button>
                <button className="cfg-modal-close" onClick={() => setModal(null)}>×</button>
              </div>
            </div>

            <div className="prod-modal-body">

              {/* ─ SECCIÓN 1: Datos generales ─ */}
              <div className="prod-section">
                <div className="prod-section-title">Datos Generales</div>
                <div className="prod-section-body">
                  <div className="prod-field">
                    <label className="prod-label">Código / SKU</label>
                    <input className="prod-input" value={form.codigo}
                      onChange={e => set('codigo', e.target.value)} placeholder="Ej: CLV-2PUL-100" />
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">Nombre <span className="req">*</span></label>
                    <input className="prod-input" value={form.nombre}
                      onChange={e => set('nombre', e.target.value)} placeholder="Nombre completo oficial" autoFocus />
                  </div>
                  <div className="prod-field span2">
                    <label className="prod-label">Alias / Nombre corto <span style={{ color: '#C9A84C', fontWeight: 400 }}>(IMPORTANTE para búsqueda en ventas)</span></label>
                    <input className="prod-input" value={form.alias}
                      onChange={e => set('alias', e.target.value)} placeholder="Ej: CLAVOS 2, CLAVO PUNTA, etc." />
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">Familia</label>
                    <select className="prod-select" value={form.familia_id} onChange={e => set('familia_id', e.target.value)}>
                      <option value="">-- Sin familia --</option>
                      {familias.map(f => <option key={f.id} value={String(f.id)}>{f.nombre}</option>)}
                    </select>
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">Marca</label>
                    <select className="prod-select" value={form.marca_id} onChange={e => set('marca_id', e.target.value)}>
                      <option value="">-- Sin marca --</option>
                      {marcas.map(m => <option key={m.id} value={String(m.id)}>{m.nombre}</option>)}
                    </select>
                  </div>
                  <div className="prod-field span2">
                    <label className="prod-label">Descripción</label>
                    <textarea className="prod-textarea" value={form.descripcion}
                      onChange={e => set('descripcion', e.target.value)} placeholder="Descripción adicional..." rows={2} />
                  </div>
                </div>
              </div>

              {/* ─ SECCIÓN 2: Unidades y precios ─ */}
              <div className="prod-section">
                <div className="prod-section-title">Unidades &amp; Precios</div>
                <div className="prod-section-body" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'7px 12px',padding:'8px 12px'}}>
                  <div className="prod-field">
                    <label className="prod-label">Unidad de Compra <span className="req">*</span></label>
                    <select className="prod-select" value={form.unidad_compra_id} onChange={e => set('unidad_compra_id', e.target.value)}>
                      <option value="">-- Seleccionar --</option>
                      {unidades.map(u => <option key={u.id} value={String(u.id)}>{u.short_name} — {u.name}</option>)}
                    </select>
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">Unidad Mínima <span className="req">*</span></label>
                    <select className="prod-select" value={form.unidad_minima_id} onChange={e => set('unidad_minima_id', e.target.value)}>
                      <option value="">-- Seleccionar --</option>
                      {unidades.map(u => <option key={u.id} value={String(u.id)}>{u.short_name} — {u.name}</option>)}
                    </select>
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">Factor (1 U.Compra = ? U.Mínima)</label>
                    <input className="prod-input" type="number" min="1" step="any" value={form.factor_conversion}
                      onChange={e => set('factor_conversion', e.target.value)} placeholder="Ej: 100" />
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">Precio Compra <span style={{color:'#6b7280',fontWeight:400,textTransform:'none'}}>(por U.Compra)</span></label>
                    <input className="prod-input" type="number" min="0" step="0.0001" value={form.precio_compra}
                      onChange={e => set('precio_compra', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">P. Venta Minorista <span style={{color:'#6b7280',fontWeight:400,textTransform:'none'}}>(por U.Compra)</span></label>
                    <input className="prod-input" type="number" min="0" step="0.0001" value={form.precio_venta}
                      onChange={e => set('precio_venta', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">P. Venta Mayorista <span style={{color:'#6b7280',fontWeight:400,textTransform:'none'}}>(por U.Compra)</span></label>
                    <input className="prod-input" type="number" min="0" step="0.0001" value={form.precio_mayorista}
                      onChange={e => set('precio_mayorista', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">P. Venta U.Mínima <span style={{color:'#6b7280',fontWeight:400,textTransform:'none'}}>(por U.Mínima)</span></label>
                    <input className="prod-input" type="number" min="0" step="0.0001" value={form.precio_minimo}
                      onChange={e => set('precio_minimo', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="prod-field">
                    <label className="prod-label">Stock Mínimo <span style={{color:'#6b7280',fontWeight:400,textTransform:'none'}}>(en U.Mínima)</span></label>
                    <input className="prod-input" type="number" min="0" step="any" value={form.stock_minimo}
                      onChange={e => set('stock_minimo', e.target.value)} placeholder="0" />
                  </div>
                </div>
              </div>

              {/* ─ SECCIÓN 3: Presentaciones de venta ─ */}
              <div className="prod-section">
                <div className="prod-section-title">Presentaciones de Venta (Fracciones)</div>
                <div style={{ padding: '7px 12px' }}>
                  <table className="pres-table">
                    <thead>
                      <tr>
                        <th className="pres-th">Nombre presentación</th>
                        <th className="pres-th">Unidad</th>
                        <th className="pres-th">Factor (en U.Mín.)</th>
                        <th className="pres-th">Precio venta</th>
                        <th className="pres-th" title="Presentación por defecto">Default</th>
                        <th className="pres-th"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.presentaciones.length === 0 && (
                        <tr><td colSpan={6} className="pres-empty">Sin presentaciones — agrega al menos una</td></tr>
                      )}
                      {form.presentaciones.map((p, idx) => (
                        <tr key={idx}>
                          <td className="pres-td">
                            <input className="pres-input" value={p.nombre}
                              onChange={e => updatePres(idx, 'nombre', e.target.value)} placeholder="Ej: CAJA, MEDIA CAJA, DECENA" />
                          </td>
                          <td className="pres-td">
                            <select className="pres-input" value={p.unidad_medida_id}
                              onChange={e => updatePres(idx, 'unidad_medida_id', e.target.value)} style={{ minWidth: 80 }}>
                              <option value="">—</option>
                              {unidades.map(u => <option key={u.id} value={String(u.id)}>{u.short_name}</option>)}
                            </select>
                          </td>
                          <td className="pres-td">
                            <input className="pres-input w70" type="number" min="0.0001" step="any"
                              value={p.factor} onChange={e => updatePres(idx, 'factor', e.target.value)} />
                          </td>
                          <td className="pres-td">
                            <input className="pres-input w90" type="number" min="0" step="0.0001"
                              value={p.precio_venta} onChange={e => updatePres(idx, 'precio_venta', e.target.value)}
                              placeholder="Auto" title="Dejar vacío para calcular automático" />
                          </td>
                          <td className="pres-td" style={{ textAlign: 'center' }}>
                            <input type="radio" className="pres-default-radio"
                              checked={!!p.es_default} onChange={() => setPresDefault(idx)} />
                          </td>
                          <td className="pres-td">
                            <button className="pres-btn-del" onClick={() => removePres(idx)} title="Eliminar">×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Fila para agregar nueva presentación */}
                  <div className="pres-add-row" style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                    <input className="pres-input" style={{ flex: 2, minWidth: 130 }} value={newPres.nombre}
                      onChange={e => setNewPres(p => ({ ...p, nombre: e.target.value }))}
                      placeholder="Nombre (Ej: CAJA)" onKeyDown={e => e.key === 'Enter' && addPresentacion()} />
                    <select className="pres-input" style={{ flex: 1, minWidth: 80 }} value={newPres.unidad_medida_id}
                      onChange={e => setNewPres(p => ({ ...p, unidad_medida_id: e.target.value }))}>
                      <option value="">Unidad</option>
                      {unidades.map(u => <option key={u.id} value={String(u.id)}>{u.short_name}</option>)}
                    </select>
                    <input className="pres-input w70" type="number" min="0.0001" step="any"
                      value={newPres.factor} placeholder="Factor"
                      onChange={e => setNewPres(p => ({ ...p, factor: e.target.value }))} />
                    <input className="pres-input w90" type="number" min="0" step="0.0001"
                      value={newPres.precio_venta} placeholder="Precio"
                      onChange={e => setNewPres(p => ({ ...p, precio_venta: e.target.value }))} />
                    <button className="pres-btn-add" onClick={addPresentacion}>
                      <FiPlus size={13} /> Agregar
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                    Factor = cuántas unidades mínimas equivale esta presentación &nbsp;·&nbsp; Precio vacío = se calcula automático (precio_venta_base × factor)
                  </div>
                </div>
              </div>

            </div>

            {/* ── PANEL DE AYUDA ── */}
            {showHelp && (
              <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.55)',
                            zIndex:10, display:'flex', alignItems:'center', justifyContent:'center',
                            borderRadius: 10 }}
                   onClick={() => setShowHelp(false)}>
                <div style={{ background:'#fff', borderRadius:10, width:'96%', maxHeight:'88%',
                              overflow:'auto', padding:'0 0 16px', boxShadow:'0 8px 32px rgba(0,0,0,.25)' }}
                     onClick={e => e.stopPropagation()}>

                  {/* Cabecera ayuda */}
                  <div style={{ background:'#1e3a5f', color:'#fff', padding:'12px 16px',
                                display:'flex', justifyContent:'space-between', alignItems:'center',
                                borderRadius:'10px 10px 0 0' }}>
                    <span style={{ fontWeight:700, fontSize:14 }}>
                      <FiHelpCircle size={14} style={{ marginRight:6, verticalAlign:'middle' }}/>
                      Ejemplo completo — CLAVOS 2 PULGADAS
                    </span>
                    <button onClick={() => setShowHelp(false)}
                      style={{ background:'none', border:'none', color:'#fff', fontSize:20, cursor:'pointer' }}>
                      <FiX />
                    </button>
                  </div>

                  <div style={{ padding:'14px 18px', fontSize:12.5, lineHeight:1.6 }}>

                    {/* SECCIÓN 1 */}
                    <div style={{ marginBottom:14 }}>
                      <div style={{ background:'#1e3a5f', color:'#fff', padding:'4px 10px',
                                    borderRadius:5, fontWeight:700, fontSize:11.5, marginBottom:8 }}>
                        1 — Datos Generales
                      </div>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                          <tr style={{ background:'#f1f5f9' }}>
                            <th style={thS}>Campo</th>
                            <th style={thS}>Valor de ejemplo</th>
                            <th style={thS}>Explicación</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['Código / SKU',   'CLV-2PUL',                   'Código interno o de barras del producto'],
                            ['Nombre',         'CLAVOS 2 PULGADAS',          'Nombre oficial completo (aparece en documentos)'],
                            ['Alias',          'CLAVO 2  /  CLV2  /  CL2P', 'Nombres cortos para búsqueda rápida en ventas — separa con /'],
                            ['Familia',        'FERRETERÍA',                  'Categoría del producto'],
                            ['Marca',          'POINT',                       'Marca del fabricante'],
                          ].map(([c,v,e], i) => (
                            <tr key={i} style={{ background: i%2===0 ? '#fff':'#f8fafc' }}>
                              <td style={tdS}><strong>{c}</strong></td>
                              <td style={{ ...tdS, color:'#1d4ed8', fontFamily:'monospace' }}>{v}</td>
                              <td style={{ ...tdS, color:'#475569' }}>{e}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* SECCIÓN 2 */}
                    <div style={{ marginBottom:14 }}>
                      <div style={{ background:'#1e3a5f', color:'#fff', padding:'4px 10px',
                                    borderRadius:5, fontWeight:700, fontSize:11.5, marginBottom:8 }}>
                        2 — Unidades &amp; Precios
                      </div>
                      <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:6,
                                    padding:'8px 12px', marginBottom:8, fontSize:12 }}>
                        <strong>Regla clave:</strong> el stock siempre se guarda en <strong>Unidad Mínima</strong>.
                        La Unidad de Compra es solo para ingresarlo del proveedor.
                        El <strong>Factor</strong> convierte entre ambas.
                      </div>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                          <tr style={{ background:'#f1f5f9' }}>
                            <th style={thS}>Campo</th>
                            <th style={thS}>Valor</th>
                            <th style={thS}>Explicación</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['Unidad de Compra',     'CAJA (CJA)',  'Así viene del proveedor — por CAJA'],
                            ['Unidad Mínima',        'UNIDAD (UND)','La unidad más pequeña que se vende o almacena'],
                            ['Factor conversión',    '100',         '1 CAJA contiene 100 UNIDADES → ingresa 100'],
                            ['Precio Compra',        'S/ 45.00',    'Costo de 1 CAJA al proveedor'],
                            ['P. Venta Minorista',   'S/ 50.00',    'Precio de venta de 1 CAJA al público'],
                            ['P. Venta Mayorista',   'S/ 48.00',    'Precio de venta por volumen (1 CAJA)'],
                            ['P. Venta U. Mínima',   'S/ 0.60',     'Precio de venta por 1 UNIDAD suelta'],
                            ['Stock Mínimo',         '200',         'Alerta cuando el stock baje de 200 UNIDADES'],
                          ].map(([c,v,e], i) => (
                            <tr key={i} style={{ background: i%2===0 ? '#fff':'#f8fafc' }}>
                              <td style={tdS}><strong>{c}</strong></td>
                              <td style={{ ...tdS, color:'#16a34a', fontFamily:'monospace', fontWeight:700 }}>{v}</td>
                              <td style={{ ...tdS, color:'#475569' }}>{e}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* SECCIÓN 3 */}
                    <div>
                      <div style={{ background:'#1e3a5f', color:'#fff', padding:'4px 10px',
                                    borderRadius:5, fontWeight:700, fontSize:11.5, marginBottom:8 }}>
                        3 — Presentaciones de Venta (Fracciones)
                      </div>
                      <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6,
                                    padding:'8px 12px', marginBottom:8, fontSize:12 }}>
                        <strong>¿Qué es el Factor aquí?</strong> Cuántas <strong>Unidades Mínimas</strong> (UND) tiene esa presentación.
                        Si el precio se deja vacío, se calcula solo: <code style={{ background:'#dbeafe', padding:'1px 5px', borderRadius:3 }}>Precio = P.Venta U.Mínima × Factor</code>
                      </div>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                          <tr style={{ background:'#f1f5f9' }}>
                            <th style={thS}>Nombre</th>
                            <th style={thS}>Unidad</th>
                            <th style={thS}>Factor (en UND)</th>
                            <th style={thS}>Precio venta</th>
                            <th style={thS}>Default</th>
                            <th style={thS}>Cálculo automático</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['CAJA',       'CJA', '100', 'S/ 50.00', '✓', '0.60 × 100 = 60.00 (pero se puso fijo 50.00)'],
                            ['MEDIA CAJA', 'CJA', '50',  '(vacío)',  '',  '0.60 × 50  = S/ 30.00 ← auto'],
                            ['DOCENA',     'DOC', '12',  '(vacío)',  '',  '0.60 × 12  = S/ 7.20 ← auto'],
                            ['UNIDAD',     'UND', '1',   '(vacío)',  '',  '0.60 × 1   = S/ 0.60 ← auto'],
                          ].map(([n,u,f,p,d,calc], i) => (
                            <tr key={i} style={{ background: i%2===0 ? '#fff':'#f8fafc' }}>
                              <td style={{ ...tdS, fontWeight:700, color:'#1e293b' }}>{n}</td>
                              <td style={{ ...tdS, fontFamily:'monospace', color:'#1d4ed8' }}>{u}</td>
                              <td style={{ ...tdS, fontFamily:'monospace', color:'#7c3aed', fontWeight:700 }}>{f}</td>
                              <td style={{ ...tdS, fontFamily:'monospace', color:'#16a34a' }}>{p}</td>
                              <td style={{ ...tdS, textAlign:'center', color:'#16a34a', fontSize:15 }}>{d}</td>
                              <td style={{ ...tdS, color:'#64748b', fontSize:11 }}>{calc}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={{ marginTop:10, background:'#f0fdf4', border:'1px solid #bbf7d0',
                                    borderRadius:6, padding:'8px 12px', fontSize:11.5, color:'#15803d' }}>
                        <strong>Pasos para agregar una presentación:</strong>
                        &nbsp;① Escribe el nombre (ej: <em>CAJA</em>) →
                        &nbsp;② Selecciona la unidad →
                        &nbsp;③ Ingresa el factor (cuántas UND contiene) →
                        &nbsp;④ Precio opcional (vacío = auto) →
                        &nbsp;⑤ Clic en <strong>Agregar</strong> o presiona <kbd style={{ background:'#dcfce7', padding:'1px 5px', borderRadius:3 }}>Enter</kbd>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )}

            {modalError && <div className="cfg-modal-error" style={{ margin: '0 1.4rem' }}>{modalError}</div>}

            <div className="cfg-modal-footer">
              <button className="cfg-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : modal.mode === 'new' ? 'Guardar' : 'Modificar'}
              </button>
              <button className="cfg-btn-cancel" onClick={() => setModal(null)}>× Salir</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL IMPORTAR EXCEL ══ */}
      {importModal && (
        <div className="cfg-modal-overlay">
          <div className="cfg-modal" onClick={e => e.stopPropagation()}
               style={{ width: '90vw', maxWidth: 900, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>

            <div className="cfg-modal-header">
              <span><FiUpload size={14} style={{ marginRight: 6, verticalAlign: 'middle' }}/>
                Importar Productos desde Excel
              </span>
              <button className="cfg-modal-close" onClick={() => { setImportModal(null); setAlmacenImport(''); }}>×</button>
            </div>

            <div style={{ padding: '12px 16px', overflowY: 'auto', flex: 1 }}>

              {/* Resultado post-importación parcial */}
              {importModal.postImport && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                              padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#92400e' }}>
                  <strong>{importModal.postImport.creados} producto(s) importados correctamente.</strong>
                  {importModal.postImport.con_stock > 0 && (
                    <span> ({importModal.postImport.con_stock} con stock inicial asignado)</span>
                  )}
                  {' '}Las siguientes filas fallaron y deben corregirse:
                </div>
              )}

              {/* Resumen */}
              {!importModal.postImport && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8,
                              padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiCheckCircle size={18} color="#15803d" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>
                    {importModal.rows.length} fila(s) válidas
                  </span>
                </div>
                {importModal.errors.length > 0 && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
                                padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiAlertCircle size={18} color="#dc2626" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
                      {importModal.errors.length} fila(s) con errores — serán omitidas
                    </span>
                  </div>
                )}
              </div>
              )}

              {/* Selector almacén para stock inicial */}
              {!importModal.postImport && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8,
                              padding: '10px 16px', marginBottom: 14, display: 'flex',
                              alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#0369a1', whiteSpace: 'nowrap' }}>
                    Almacén destino (stock inicial):
                  </label>
                  <select
                    value={almacenImport}
                    onChange={e => setAlmacenImport(e.target.value)}
                    style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #7dd3fc',
                             borderRadius: 5, background: '#fff', minWidth: 180 }}
                  >
                    <option value="">— Sin stock inicial —</option>
                    {almacenes.map(a => (
                      <option key={a.id} value={a.id}>{a.nombre}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: 11, color: '#0369a1' }}>
                    Solo aplica si el Excel incluye la columna <strong>stock_inicial</strong> con valores &gt; 0.
                  </span>
                </div>
              )}

              {/* Tabla errores */}
              {importModal.errors.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ background: '#dc2626', color: '#fff', padding: '4px 10px',
                                borderRadius: '6px 6px 0 0', fontSize: 12, fontWeight: 700 }}>
                    Filas con errores (no se importarán)
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12,
                                  border: '1px solid #fecaca', borderTop: 'none' }}>
                    <thead>
                      <tr style={{ background: '#fff5f5' }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', width: 60 }}>Fila</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left' }}>Nombre</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left' }}>Errores</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importModal.errors.map((e, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fff5f5' }}>
                          <td style={{ padding: '5px 10px', color: '#dc2626', fontWeight: 700 }}>{e.fila}</td>
                          <td style={{ padding: '5px 10px' }}>{e.nombre}</td>
                          <td style={{ padding: '5px 10px', color: '#dc2626' }}>
                            {e.errores.join(' · ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tabla preview de válidos */}
              {importModal.rows.length > 0 && (() => {
                const hasStock = importModal.rows.some(r => r.stock_inicial > 0);
                const headers = ['Fila','Código','Nombre','Alias','Familia','Marca','U.Compra','U.Mínima','Factor','P.Compra','P.Venta','Stk.Mín.'];
                if (hasStock) headers.push('Stk.Ini.');
                return (
                  <div>
                    <div style={{ background: '#15803d', color: '#fff', padding: '4px 10px',
                                  borderRadius: '6px 6px 0 0', fontSize: 12, fontWeight: 700 }}>
                      Productos a importar
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12,
                                      border: '1px solid #86efac', borderTop: 'none' }}>
                        <thead>
                          <tr style={{ background: '#f0fdf4' }}>
                            {headers.map(h => (
                              <th key={h} style={{ padding: '5px 8px', textAlign: 'left', whiteSpace: 'nowrap', fontSize: 11 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importModal.rows.map((r, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f0fdf4' }}>
                              <td style={{ padding: '4px 8px', color: '#64748b' }}>{r._fila}</td>
                              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{r.codigo || '—'}</td>
                              <td style={{ padding: '4px 8px', fontWeight: 600 }}>{r.nombre}</td>
                              <td style={{ padding: '4px 8px', color: '#475569' }}>{r.alias || '—'}</td>
                              <td style={{ padding: '4px 8px' }}>{r.familia || '—'}</td>
                              <td style={{ padding: '4px 8px' }}>{r.marca || '—'}</td>
                              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{r.unidad_compra}</td>
                              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{r.unidad_minima}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.factor_conversion}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.precio_compra || '—'}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.precio_venta || '—'}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.stock_minimo || '—'}</td>
                              {hasStock && (
                                <td style={{ padding: '4px 8px', textAlign: 'right',
                                             fontWeight: r.stock_inicial > 0 ? 700 : 400,
                                             color: r.stock_inicial > 0 ? '#15803d' : '#94a3b8' }}>
                                  {r.stock_inicial > 0 ? r.stock_inicial : '—'}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {importModal.rows.length === 0 && importModal.errors.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
                  El archivo no contiene filas de datos.
                </div>
              )}
            </div>

            <div className="cfg-modal-footer">
              {importModal.rows.length > 0 && (
                <button className="cfg-btn-save" onClick={handleConfirmImport} disabled={importing}>
                  {importing ? 'Importando...' : `Importar ${importModal.rows.length} producto(s)`}
                </button>
              )}
              <button className="cfg-btn-cancel" onClick={() => { setImportModal(null); setAlmacenImport(''); }}>
                × Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
